import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SEOHead } from '../../../components/seo/SEOHead';
import { DataTable } from '../../../components/admin/DataTable';
import { Spinner } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { api } from '../../../lib/api';
import type { Service } from '@bharat-consultancy/shared';

export default function ServiceList() {
  const [services, setServices] = useState<Service[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.admin.services
      .list()
      .then((res) => setServices(res.services))
      .catch(() => setServices([]))
      .finally(() => setIsLoading(false));
  }, []);

  async function togglePublish(service: Service) {
    const newStatus = service.status === 'published' ? 'draft' : 'published';
    try {
      await api.admin.services.updateStatus(service.id, newStatus);
      setServices((prev) => prev.map((s) => (s.id === service.id ? { ...s, status: newStatus } : s)));
    } catch {
      alert('Could not update service status. Please try again.');
    }
  }

  return (
    <>
      <SEOHead title="Services — Admin" description="" canonicalPath="/admin/services" />

      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold text-ink">Services</h1>
        <Button size="sm" onClick={() => navigate('/admin/services/new')}>
          + Add Service
        </Button>
      </div>

      <div className="mt-4">
        {isLoading ? (
          <Spinner className="h-6 w-6 text-seal-blue" />
        ) : (
          <DataTable
            columns={[
              { header: 'Title', render: (s) => s.title },
              { header: 'Starting Price', render: (s) => `₹${s.startingPrice.toLocaleString('en-IN')}` },
              {
                header: 'Status',
                render: (s) => (
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-xs ${
                      s.status === 'published' ? 'bg-ledger-green/10 text-ledger-green' : 'bg-ink/5 text-ink/60'
                    }`}
                  >
                    {s.status}
                  </span>
                ),
              },
              {
                header: 'Actions',
                render: (s) => (
                  <button
                    className="text-sm font-medium text-seal-blue hover:underline"
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePublish(s);
                    }}
                  >
                    {s.status === 'published' ? 'Unpublish' : 'Publish'}
                  </button>
                ),
              },
            ]}
            rows={services}
            keyExtractor={(s) => s.id}
            onRowClick={(s) => navigate(`/admin/services/${s.slug}`)}
            emptyMessage="No services yet."
          />
        )}
      </div>
    </>
  );
}
